/**
 * Connect use-case prompts as templates — pure, no React.
 *
 * A template is a use case's question with its company-specific words marked
 * as slots: `{key|fallback}`. DANI asks one question per slot
 * (`questions.ts`) and fills each from the answer. A slot left unanswered,
 * or answered "Let DANI choose", prints its fallback, which is the wording
 * the prompt used before it had slots.
 *
 *   'Weekly visit trend for {brand|my} stores vs. {competitors|the two nearest competitors}'
 */

export type SlotKey =
    | 'brand'
    | 'competitors'
    | 'markets'
    | 'window'
    | 'survey'
    | 'question'
    | 'joinWindow'
    | 'audience'
    | 'category';

const SLOT_KEYS: readonly SlotKey[] = [
    'brand',
    'competitors',
    'markets',
    'window',
    'survey',
    'question',
    'joinWindow',
    'audience',
    'category',
];

function isSlotKey(key: string): key is SlotKey {
    return (SLOT_KEYS as readonly string[]).includes(key);
}

export type TemplateSegment =
    | { type: 'text'; text: string }
    | { type: 'slot'; key: SlotKey; fallback: string };

const SLOT_PATTERN = /\{(\w+)(?:\|([^}]*))?\}/g;

/** Splits a template into text and slots. A `{name}` that is not a known
 * slot is kept as literal text, so a typo shows up in the prompt (and in
 * the template test) rather than silently vanishing. */
export function parseTemplate(template: string): TemplateSegment[] {
    const segments: TemplateSegment[] = [];
    let last = 0;
    for (const match of template.matchAll(SLOT_PATTERN)) {
        const [whole, key, fallback = ''] = match;
        const start = match.index ?? 0;
        if (start > last) segments.push({ type: 'text', text: template.slice(last, start) });
        segments.push(
            isSlotKey(key) ? { type: 'slot', key, fallback } : { type: 'text', text: whole },
        );
        last = start + whole.length;
    }
    if (last < template.length) segments.push({ type: 'text', text: template.slice(last) });
    return segments;
}

/** The slots a template uses, in the order they appear, each once. */
export function templateSlots(template: string): SlotKey[] {
    const keys: SlotKey[] = [];
    for (const segment of parseTemplate(template)) {
        if (segment.type === 'slot' && !keys.includes(segment.key)) keys.push(segment.key);
    }
    return keys;
}

/** "Walmart", "Walmart and Costco", "Walmart, Costco and Kroger". */
export function joinList(items: readonly string[]): string {
    if (items.length <= 1) return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** What each slot prints. A list joins as "A, B and C"; a missing or blank
 * value prints the slot's fallback. */
export type SlotValues = Partial<Record<SlotKey, string | readonly string[]>>;

function slotValue(key: SlotKey, values: SlotValues): string {
    const value = values[key];
    if (value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    return joinList(value.map((item) => item.trim()).filter(Boolean));
}

function tidy(text: string): string {
    return text
        .replace(/\s+/g, ' ')
        .replace(/\s+([,.?!])/g, '$1')
        .trim();
}

/** The question DANI is asked: each slot prints its value, or its fallback
 * when the value is missing or blank. */
export function fillTemplate(template: string, values: SlotValues): string {
    return tidy(
        parseTemplate(template)
            .map((segment) =>
                segment.type === 'text'
                    ? segment.text
                    : slotValue(segment.key, values) || segment.fallback,
            )
            .join(''),
    );
}
